import moment from 'moment';
import 'moment-timezone';

moment.locale('sv');

export const ROLES = {
    GUEST: -1,
    USER: 1,
    ORGANIZATION_USER: 2,
    ORGANIZATION_ADMIN: 3,
    ORGANIZATION_TEAM_ADMIN: 5,
    CERTIFIER: 4,
    SUPER_ADMIN: 99,
};

/**
 * Update the user storage
 *
 * @param { Object } obj
 */
export function userStorageUpdate(obj) {
    const user = getCache();

    if (user) {
        const array = Object.keys(obj);

        array.forEach((key) => {
            user[key] = obj[key];
        });

        localStorage.setItem('system_cache', JSON.stringify(user));
    }
}

/**
 * Certificates Templates
 */

export const certificatesTemplates = [
    {
        index: 0,
        name: '.xlsx',
        template: 'certificate-templates/falagrett-importmall-certifikat.xlsx',
    },
];

/**
 * Today
 */
export const today = moment().format('YYYY-MM-DD');

/**
 * Format Timestamp as moment
 * @param {Timestamp|Date} date
 * @param {String} format
 * @returns {String} Formated date string
 */
export const formatDate = (date, format) => {
    if (!format) {
        format = 'YYYY-MM-DD';
    }

    if (typeof date === 'string' || date instanceof String) {
        // Stop moment from complaining about invalid date
        date = new Date(date);
    }

    return moment(date).tz('Europe/Stockholm').format(format);
};

/**
 * Get current date three months back
 * @returns {String} Formated date string
 */
export const in3months = () => {
    return moment().add(3, 'M').format('YYYY-MM-DD');
};

export const inMonths = (months) => {
    return moment().add(months, 'M').format('YYYY-MM-DD');
};

/**
 * Returns string as camelCase
 * @param {Stirng} str string to camalize
 * @returns {String} camalized string
 */
export const camalize = (str) => {
    return str.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase());
};

export const capitalizeFirstLetter = (str) => {
    return str[0].toUpperCase() + str.slice(1);
};

/**
 * Api-switch that tells you where BankID is in the login-process.
 * @param {String} hintCode current response from api
 * @param intent
 * @returns {Object} response based on hintCode
 */
export const checkHintCode = (hintCode, intent = 'default') => {
    let obj = {
        hintCode: hintCode,
        status: null,
        error: null,
        snackbar: null,
    };

    switch (hintCode) {
        case 'cervdSuccessLogin':
            obj.status = true;
            obj.error = false;
            break;
        case 'transactionCanceled':
            obj.status = false;
            obj.error = true;
            obj.snackbar = {
                status: 'Error',
                message: 'Inloggningen avbröts. Försök igen',
            };
            break;
        case 'userCancel':
            obj.status = false;
            obj.error = true;
            obj.snackbar = {
                status: 'Error',
                message: 'Inloggningen avbröts. Försök igen',
            };
            break;
        case 'expiredTransaction':
            obj.status = false;
            obj.error = true;
            obj.snackbar = {
                status: 'Error',
                title: 'Inloggningen avbruten.',
                message: 'Din session har löpt ut. Försök igen',
            };
            break;
        case 'cervdNoUser':
            obj.status = false;
            obj.error = true;

            if (intent !== 'register') {
                obj.snackbar = {
                    status: 'Warning',
                    message: 'Finns inget konto med dessa uppgifter, vänligen registrera dig',
                    btnText: 'Registrera här',
                    btnHref: () => (window.location = '/register'),
                };
            }
            break;
        case 'cervdUserAlreadyExists':
            obj.status = false;
            obj.error = true;
            obj.snackbar = {
                status: 'Error',
                message: 'Ett konto med detta personnummer finns redan, vänligen logga in',
                btnText: 'Logga in här',
                btnHref: () => (window.location = '/'),
            };
            break;
    }

    return obj;
};

/**
 * Check if we have logged-in user.
 * @returns {Boolean}
 */
export const authUser = () => {
    const cache = getCache();

    return !!(cache && 'string' === typeof cache.token && cache.national_identity_number);
};

export const SYSTEM_ERROR_EVENT = 'cervd.system_error';

/**
 * Fired when the cached login session is removed because it expired or is
 * corrupt. Boot.vue listens and resets the in-memory state to match, so no
 * component keeps acting on a login that no longer exists in storage.
 */
export const SESSION_EXPIRED_EVENT = 'cervd.session_expired';
export const SESSION_EXPIRED_MESSAGE = 'Din session löpte ut, vänligen logga in på nytt.';

/**
 * Dispatch a system error.
 * @returns { Object }
 */
export const dispatchSystemError = (message) => {
    const event = new CustomEvent(SYSTEM_ERROR_EVENT, { detail: message });
    window.dispatchEvent(event);
};

/**
 * Get the cache from localStorage.
 * @returns { Object }
 */
export const getCache = (key = 'system_cache') => {
    let cache = localStorage.getItem(key);

    const deleteCacheAndShowError = (reason = '') => {
        if (reason) {
            console.debug(reason);
        }
        dispatchSystemError(SESSION_EXPIRED_MESSAGE);

        removeCache(key);

        if (key === 'system_cache') {
            window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { detail: reason }));
        }
    };

    if (cache) {
        try {
            cache = JSON.parse(cache);
        } catch {
            deleteCacheAndShowError('Error parsing cache');
            return null;
        }

        if (cache.expires_at < new Date().getTime()) {
            deleteCacheAndShowError('Cache session expired');
            return null;
        }

        return cache;
    }

    return null;
};

/**
 * Set cache in localStorage.
 * @returns { Object }
 * @param obj
 * @param key
 */
export const setCache = (obj, key = 'system_cache') => {
    if (!obj || typeof obj !== 'object') {
        // Nothing to persist; the caller lost its session while working (see getCache).
        console.debug(`Skipped writing empty ${key} cache`);
        return;
    }

    // Set 4 hour expiration
    obj.expires_at = moment().add(4, 'hour').format('x');
    localStorage.setItem(key, JSON.stringify(obj));
};

/**
 * Remove the cache from localStorage
 * @returns { Boolean }
 */
export const removeCache = (key = 'system_cache') => {
    localStorage.removeItem(key);

    return !getCache();
};

/**
 * Prepare certificate data.
 * @param {Array} array - Certificates
 * @param {Array} newAdded - Recent added certificates
 * @returns {Object} - All certificates, all togehter and just current certificates to show
 */
export const processCertificatesArray = (array, newAdded = []) => {
    let data = [];
    let dataShow = [];

    if (array.length > 0) {
        array.forEach((item) => {
            item.new = false;
            item.selected = false;
            item.ssg_status = item.meta?.ssg_id ?? '';
            item.issued_at_display = formatDate(item.issued_at);
            (item.imported_at_display = item.imported_at ? formatDate(item.imported_at) : null),
                (item.expires_at_display = item.expires_at ? formatDate(item.expires_at) : 'Tills vidare');

            // Is the cert outgoing?
            item.outgoing = item.expires_at && item.expires_at <= in3months();

            // if (index <= 25) {
            // Let the current client side paginagion handle what's showing and not
            dataShow.push(item);
            // }

            data.push(item);
        });

        if (newAdded.length > 0) {
            newAdded.forEach((item) => {
                const pos = data.findIndex((obj) => obj && obj.id === item.id);

                if (pos >= 0) {
                    data[pos].outgoing = data[pos].expires_at && data[pos].expires_at <= in3months();
                    data[pos].new = true;
                }
            });
        }
    }

    return {
        certificates: data,
        // Sort so the newly added get first
        show: dataShow.sort((x, y) => Number(y.new) - Number(x.new)),
    };
};

/**
 * Get preselected certificates
 * @returns {Array} List of selected or empty array
 */
export const getSelectedStorage = () => {
    const selected = sessionStorage.getItem('cervd_data');

    return selected ? JSON.parse(selected) : [];
};

/**
 * Set preselected certificates
 * @param {Array} data List of preselected certificates
 */
export const setSelectedStorage = (data) => sessionStorage.setItem('cervd_data', JSON.stringify(data));

/**
 * Remove preselected certificates
 */
export const removeSelectedStorage = () => sessionStorage.removeItem('cervd_data');

/**
 * Checks the response of the current profile if completed or not
 * @param {Object} response data response
 * @returns {Boolean} True, user is uncompleted, false, user is okay
 */
export const isIncompleteProfile = (response) => response?.data?.has_incomplete_profile; //response.status === 'Error' && response.message === 'incomplete_user';

/**
 * Update localStorage
 * @param {String} key key of item to update
 * @param {String} value value to be set
 */
export const setLocalItem = (key, value) => {
    let cache = getCache();

    if (!cache) {
        cache = {};
    }

    cache[key] = value;
    setCache(cache);
};

/**
 * Format to Swedish currency
 * @param {Number} price
 * @returns {String} Formatted price in SEK
 */
export const formatCurrency = (price) => {
    const formatter = new Intl.NumberFormat('sv-SE', {
        style: 'currency',
        currency: 'SEK',
    });

    return formatter.format(price).replace(',00', '');
};

export const isMobileOrTablet = () => {
    let check = false;

    (function (a) {
        if (
            /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series([46])0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
                a
            ) ||
            /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br([ev])w|bumb|bw-([nu])|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do([cp])o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly([-_])|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-([mpt])|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c([- _agpst])|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac([ \-/])|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja([tv])a|jbro|jemu|jigs|kddi|keji|kgt([ /])|klon|kpt |kwc-|kyo([ck])|le(no|xi)|lg( g|\/([klu])|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t([- ov])|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30([02])|n50([025])|n7(0([01])|10)|ne(([cm])-|on|tf|wf|wg|wt)|nok([6i])|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan([adt])|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c([-01])|47|mc|nd|ri)|sgh-|shar|sie([-m])|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel([im])|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c([- ])|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(
                a.substr(0, 4)
            )
        ) {
            check = true;
        }
    })(navigator.userAgent || navigator.vendor || window.opera);

    return check;
};

export const setSnackbar = (response, confetti = false) => {
    let snackbar = {
        status: response.status,
        message: response.message,
        confetti: confetti,
    };

    if (response.status === 'Warning') {
        snackbar.message = response.message;
    }

    if (response.status === 'Error') {
        // Always make sure to hide confetti when error is shown
        snackbar.confetti = false;

        snackbar.message = Array.isArray(response.data) ? response.data[0].message : response.message;
    }

    return snackbar;
};

export const isNumeric = (value) => /^\d+$/.test(value);

export const prepareCompetences = (array) => {
    const result = [
        {
            id: 0,
            name: 'Övrigt',
            data: [],
            selected: false,
        },
    ];

    if (!Array.isArray(array) || array.length <= 0) {
        return result;
    }

    array.forEach((item) => {
        item.toggled = false;

        if (item.category) {
            const finder = result.find((c) => c.id === item.category.id);

            if (!finder) {
                // Add props
                item.category.data = [];
                item.category.selected = false;
                // Push the category
                result.push(item.category);
            }

            // Find the index of cat.
            const index = result.findIndex((c) => c.id === item.category.id);
            // Push item into the right index of cat.
            result[index].data.push(item);
        } else {
            // Has no category, save it as others
            if (item.type === 'external') {
                result[0].data.push(item);
            }
        }
    });

    if (result[0].data.length <= 0) {
        // Unset the others-category if empty
        result.splice(0, 1);
    }

    return result.sort((a, b) => {
        if (a.name < b.name) {
            return -1;
        }

        if (a.name > b.name) {
            return 1;
        }

        return 0;
    });
};

export const getAddon = (addons, identifier) => addons?.find((a) => a.identifier === identifier);
export const withId06 = (list) =>
    list.filter((c) => c.id06_status !== 'active' && c.competence.has_id06 && !c?.outgoing);

export const setCookie = (name, value, days) => {
    var expires = '';
    if (days) {
        var date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = '; expires=' + date.toUTCString();
    }
    document.cookie = name + '=' + (value || '') + expires + '; path=/;SameSite=Lax';
};

export const getCookie = (name) => {
    var nameEQ = name + '=';
    var ca = document.cookie.split(';');
    for (var i = 0; i < ca.length; i++) {
        var c = ca[i];
        while (c.charAt(0) == ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
};

export const deleteCookie = (name) => {
    document.cookie = name + '=; Path=/;SameSite=Lax; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
};

/**
 * Short always-visible summary of a certificate's sub_competences meta
 * (a JSON string of [{id, name, description}]): first `max` names joined
 * with commas, then "+N till". Returns null for missing/corrupt snapshots.
 */
export const subCompetenceSummary = (metaValue, max = 3) => {
    if (!metaValue) return null;

    let subs;
    try {
        subs = JSON.parse(metaValue);
    } catch {
        return null;
    }
    if (!Array.isArray(subs) || !subs.length) return null;

    const names = subs.map((sub) => sub?.name).filter(Boolean);
    const overflow = names.length - max;

    return overflow > 0 ? `${names.slice(0, max).join(', ')} +${overflow} till` : names.join(', ');
};
